import React from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';

const easing = [0.22, 1, 0.36, 1];

const CourseProgressSection = ({
  user,
  studentPath,
  pathCourses,
  userProgress,
  pathProgress,
  currentStep,
  totalSteps,
  userXP = null,
  tiers = [],
  onStartCourse,
  progressLoading = false,
  needsProfileCompletion = false
}) => {
  const isCompleted = userProgress?.isPathCompleted || pathProgress >= 100;
  const firstName = user?.name?.split(' ')[0] || 'Student';
  const total = totalSteps || (pathCourses?.length || 0);
  const done = currentStep ? currentStep - 1 : 0;
  const clampedProgress = Math.max(0, Math.min(100, pathProgress || 0));

  // Calculate current tier and next tier from XP
  // Tiers are sorted by xpRequired ascending. Current tier = highest threshold the user has reached.
  const totalXP = userXP?.totalXP || 0;
  const sortedTiers = [...tiers].sort((a, b) => a.xpRequired - b.xpRequired);
  const currentTier = [...sortedTiers].reverse().find(t => totalXP >= t.xpRequired) || null;
  const currentTierIndex = currentTier ? sortedTiers.indexOf(currentTier) : -1;
  const nextTier = currentTierIndex >= 0 && currentTierIndex < sortedTiers.length - 1
    ? sortedTiers[currentTierIndex + 1]
    : null;
  const xpToNextTier = nextTier ? nextTier.xpRequired - totalXP : 0;

  const remaining = Math.max(0, total - done);
  const ringCircumference = 2 * Math.PI * 15.2;
  const ringDash = (clampedProgress / 100) * ringCircumference;

  return (
    <>
      {/* Top bar — logbook spine */}
      <div className="border-b border-border bg-background px-5 sm:px-10 py-3.5 flex items-center justify-between">
        <div className="leading-tight min-w-0">
          <p className="font-display font-bold text-foreground tracking-tight truncate text-[15px] sm:text-base">
            {studentPath?.title || 'Learning Path'}
          </p>
          {user?.group?.title && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground/55">
              {user.group.title}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {currentTier && (
            <span
              className="px-2.5 py-1 rounded-full text-[11px] font-bold text-white uppercase tracking-wide"
              style={{ backgroundColor: currentTier.color || '#1086a4' }}
            >
              {currentTier.name}
            </span>
          )}
          <span className="flex items-center gap-1.5 text-sm font-bold text-foreground tabular-nums">
            <Zap className="w-4 h-4 text-xp" />
            {totalXP.toLocaleString()} XP
          </span>
          {nextTier && (
            <span className="hidden sm:inline text-[11px] font-semibold text-foreground/45">
              {xpToNextTier.toLocaleString()} to {nextTier.name}
            </span>
          )}
        </div>
      </div>

      {/* Progress card */}
      <div className="bg-background px-5 sm:px-10 py-5 sm:py-8">
        <motion.div
          className="bg-background rounded-[20px] border border-border p-5 sm:p-7"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.08, ease: easing }}
        >
          <h1 className="font-display font-bold text-foreground tracking-tight leading-[1.22] text-[21px] sm:text-2xl">
            {isCompleted ? (
              <>Goal completed, {firstName}</>
            ) : (
              <>
                <span className="lg:hidden">Your progress</span>
                <span className="hidden lg:inline">{studentPath?.title || 'Your progress'}</span>
              </>
            )}
          </h1>

          <p className="mt-2 text-[14.5px] text-foreground/60">
            {isCompleted ? (
              <>You've finished every course on this path. Amazing work.</>
            ) : (
              <>Nice steady start &mdash; you're <span className="font-bold text-foreground tabular-nums">{clampedProgress}%</span> of the way through.</>
            )}
          </p>

          {total > 0 && (
            <div className="mt-[18px] flex items-center gap-4">
              {/* Ring — reads as a shape even at single-digit percentages, unlike a hairline bar */}
              <div className="relative w-[76px] h-[76px] flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.2" fill="none" stroke="hsl(var(--border))" strokeWidth="5" />
                  <motion.circle
                    cx="18" cy="18" r="15.2" fill="none"
                    stroke="hsl(var(--primary))" strokeWidth="5" strokeLinecap="round"
                    initial={{ strokeDasharray: `0 ${ringCircumference}` }}
                    animate={{ strokeDasharray: `${ringDash} ${ringCircumference}` }}
                    transition={{ duration: 0.9, delay: 0.35, ease: easing }}
                  />
                </svg>
                <div className="absolute inset-0 grid place-content-center text-center">
                  <span className="block font-display font-bold text-[19px] leading-none tracking-tight tabular-nums text-foreground">
                    {clampedProgress}%
                  </span>
                  <span className="block text-[11px] font-semibold text-foreground/45 mt-0.5">done</span>
                </div>
              </div>

              <div className="flex-1 min-w-0 grid gap-2">
                <p className="flex items-center gap-2.5 text-[13.5px] text-foreground/60">
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
                  <span><span className="font-bold text-foreground tabular-nums">{done}</span> {done === 1 ? 'course' : 'courses'} finished</span>
                </p>
                <p className="flex items-center gap-2.5 text-[13.5px] text-foreground/60">
                  <span className="w-2 h-2 rounded-full bg-border flex-shrink-0" aria-hidden="true" />
                  <span><span className="font-bold text-foreground tabular-nums">{remaining}</span> to go</span>
                </p>
                <p className="flex items-center gap-2.5 text-[13.5px] text-foreground/60">
                  <Zap className="w-3.5 h-3.5 text-xp flex-shrink-0" aria-hidden="true" />
                  <span><span className="font-bold text-foreground tabular-nums">{(userXP?.totalXP || 0).toLocaleString()}</span> XP earned</span>
                </p>
              </div>
            </div>
          )}

          {!isCompleted && onStartCourse && (
            <button
              onClick={onStartCourse}
              disabled={progressLoading}
              className="group mt-[18px] w-full inline-flex items-center justify-center gap-2.5 rounded-[14px] px-5 py-4 text-[15.5px] font-bold tracking-tight text-primary-foreground bg-primary hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {progressLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Loading</span>
                </>
              ) : needsProfileCompletion ? (
                <span>Complete Profile to Start</span>
              ) : (
                <>
                  <span>{userProgress?.currentContent ? 'Jump Back In' : 'Start Course'}</span>
                  <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default CourseProgressSection;
